import { createSlice } from "@reduxjs/toolkit";
import { fetchSessions, fetchSessionsV2 } from "../actions/sessionActions";
import { APISession } from "./scheduleReducer"; // Reuse existing type
import { EnrichedSession, SessionFilters } from "../../components/dashboard/sessions/types";

interface SessionsState {
  // V1 sessions (APISession[])
  sessions: APISession[];
  // V2 enriched sessions (EnrichedSession[])
  enrichedSessions: EnrichedSession[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  lastFetchTime: number;
  page_number: number;
  initialCursor: string | null; // Cursor defining the current time window
  nextCursor: string | null; // Cursor for loading more beyond the window
  hasMoreEntries: boolean;
  totalCount: number | null; // Total count of matching sessions
  // Current active filters (for V2)
  filters: SessionFilters;
  // Flag to indicate which API is being used
  useV2: boolean;
}

const initialState: SessionsState = {
  sessions: [],
  enrichedSessions: [],
  loading: false,
  loadingMore: false,
  error: null,
  lastFetchTime: 0,
  page_number: 0,
  initialCursor: null,
  nextCursor: null,
  hasMoreEntries: false,
  totalCount: null,
  filters: {},
  useV2: false,
};

const sessionsSlice = createSlice({
  name: "sessions",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setUseV2: (state, action) => {
      state.useV2 = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // V1 fetchSessions handlers
      .addCase(fetchSessions.pending, (state, action) => {
        if (action.meta.arg.page_number && action.meta.arg.page_number > 0) {
          state.loadingMore = true;
        } else {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingMore = false;

        if (action.payload.fromCache) {
          return;
        }

        // Always replace sessions with current page data for server-side pagination
        state.sessions = action.payload.data;
        state.page_number = action.payload.page_number;

        // Store initial cursor when fetching page 0 with reset
        if (action.payload.page_number === 0 && action.meta.arg.reset) {
          state.initialCursor = action.payload.cursor;
        }

        state.nextCursor = action.payload.next_cursor || null;
        state.hasMoreEntries = action.payload.has_more_entries;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchSessions.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = action.error.message || "Failed to fetch sessions";
      })
      // V2 fetchSessionsV2 handlers
      .addCase(fetchSessionsV2.pending, (state, action) => {
        if (action.meta.arg.page_number && action.meta.arg.page_number > 0) {
          state.loadingMore = true;
        } else {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchSessionsV2.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingMore = false;

        if (action.payload.fromCache) {
          return;
        }

        // Store enriched sessions
        state.enrichedSessions = action.payload.data;
        // Also store in sessions for backward compatibility (coerce type)
        state.sessions = action.payload.data as unknown as APISession[];
        state.page_number = action.payload.page_number;

        // Store initial cursor when fetching page 0 with reset
        if (action.payload.page_number === 0 && action.meta.arg.reset) {
          state.initialCursor = action.payload.cursor;
        }

        state.nextCursor = action.payload.next_cursor || null;
        state.hasMoreEntries = action.payload.has_more_entries;
        state.totalCount = action.payload.total_count ?? null;
        state.lastFetchTime = Date.now();

        // Update filters from action
        if (action.payload.filters) {
          state.filters = action.payload.filters;
        }
      })
      .addCase(fetchSessionsV2.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = action.error.message || "Failed to fetch sessions";
      });
  },
});

export const { setFilters, clearFilters, setUseV2 } = sessionsSlice.actions;
export default sessionsSlice.reducer;
